/* eslint-disable react-hooks/exhaustive-deps */
import React, { Suspense, useEffect, useState } from 'react'
import { useStore } from 'react-redux'
import PropTypes from 'prop-types'
import logger from '../common/log'
import { schedulesActions } from '../actions/schedules'
import { checkModuleSetup, verifySchedules } from '../loader/verify-module'
import { ErrorBoundary } from './error-boundary'

function createModuleContainer({
    View,
    contextCode,
    createReducer,
    fallback,
    reducers,
    schedules,
    translationsActions
}) {
    return function ModuleContainer(props) {
        const store = useStore()
        const [isVisible, setIsVisible] = useState(false)

        useEffect(() => {
            // Add schedules to the store
            if (schedules != null) {
                if (schedules instanceof Function) {
                    const effectiveSchedules = schedules(store.getState())
                    if (effectiveSchedules !== undefined) {
                        verifySchedules(effectiveSchedules)
                        store.dispatch(schedulesActions.patchSuccess(effectiveSchedules))
                    }
                } else if (typeof schedules === 'object' && Object.keys(schedules).length > 0) {
                    store.dispatch(schedulesActions.patchSuccess(schedules))
                }
            }

            // Fetch module translations and then add module-specific translation context code to the store
            if (typeof contextCode === 'string' && contextCode.length > 0 && translationsActions) {
                store.dispatch(translationsActions.updateModuleTranslations(contextCode))
            }

            // Hold the dynamically loaded reducers in Redux store
            store.loadableReducers = {
                ...reducers,
                ...store.loadableReducers
            }

            // Activate the new reducers
            store.replaceReducer(createReducer(store.loadableReducers))
            setIsVisible(true)
        }, [])

        return isVisible ? <View {...props} /> : fallback
    }
}

/*
 * Factory to create a dynamically loadable module as defined by Modux contract.
 * Modux loadable modules can expose Redux reducers to be added to the Redux store at module load time.
 * The returned Loadable React component replaces the existing Redux store by combining initial reducers,
 * all existing reducers and the reducers defined by the loaded module.
 *
 * The returned LoadableEsmComponent will lazy load the module JS bundle and its corresponding CSS bundle.
 * The location of both bundles is determined by concatenating path and suffix like so: <path>.(js|css)
 *
 * @param {Function} createReducer                   - Factory function to create the final Redux reducer
 * @param {Object} [translationsActions]             - Optional factory-created object with dispatchable translations
 *                                                     actions
 * @param {React.Component} ErrorComponent           - Component to be rendered in case of errors during module
 *                                                     initialisation
 * @param {React.Component} fallbackElement          - Element to be rendered as fallback during module loading
 * @param {string} path                              - Base path to a location, where modules can be found
 * @param {boolean} hasCss                           - Does the module has its own CSS bundle?
 * @returns {React.Component} A loadable component wrapped in an error boundary (fallback) component
 */
export const createLoadableEsmComponentFactory = (
    createReducer,
    translationsActions,
    ErrorComponent,
    fallbackElement
) => {
    if (ErrorComponent == null) {
        throw new Error('No Error fallback provided!')
    }
    if (fallbackElement == null) {
        throw new Error('No fallback element provided!')
    }
    return (path, hasCss = true) => {
        const LazyComponent = React.lazy(async () => {
            try {
                // Load module CSS bundle if it's not already loaded
                const cssLocation = `${path}.css`
                if (hasCss) {
                    loadCss(cssLocation)
                }

                // Load module JS bundle
                const resolved = await import(/* webpackIgnore: true */ `${path}.js`)

                // Verify correct module setup
                checkModuleSetup(resolved)

                // React.lazy expects to return a promise, which resolves to a module with a default export
                // containg a React component
                const returnValue = {
                    default: createModuleContainer({
                        ...resolved,
                        translationsActions,
                        createReducer,
                        fallback: fallbackElement
                    })
                }

                return returnValue
            } catch (error) {
                logger.error(`Error loading module at ${path}: `, error)
                throw error
            }
        })

        function LoadableEsmComponent(props) {
            const { children, moduleProps } = props
            return (
                // Wrap module in an error boundary to avoid crashing of the whole ui in case of any exception in
                // the module code.
                <ErrorBoundary Fallback={ErrorComponent}>
                    <Suspense fallback={fallbackElement}>
                        <LazyComponent {...moduleProps}>{children}</LazyComponent>
                    </Suspense>
                </ErrorBoundary>
            )
        }

        LoadableEsmComponent.propTypes = {
            children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
            // eslint-disable-next-line react/forbid-prop-types
            moduleProps: PropTypes.object
        }

        return LoadableEsmComponent
    }
}

function loadCss(url) {
    const cssLink = document.createElement('link')
    cssLink.id = url
    cssLink.setAttribute('rel', 'stylesheet')
    cssLink.setAttribute('type', 'text/css')
    cssLink.setAttribute('href', url)
    document.getElementsByTagName('head')[0].appendChild(cssLink)
}
